/* eslint-disable @typescript-eslint/no-explicit-any */

import { logError } from 'lib/observability';
import { getOptionalStringEnv, getStringEnvWithDefault } from 'utils/safeEnvParsing';

import { Err, Ok, Result, isErr } from './result';
import { ApiError, ErrorType, SettingsContext, SharedUserSettingsEnum, UserSettingsEnum, useSettingsStore } from './store';

const isDev = getOptionalStringEnv('NEXT_PUBLIC_IS_DEV');
const baseUrl = getStringEnvWithDefault('NEXT_PUBLIC_USER_PROFILE_API_URL', 'https://user-profile.api.speechify.com');

const SYNC_CACHE_TTL = 1000 * 60 * 60 * 1; // 1 hour
const SYNC_DEBOUNCE_TIME = 1000; // 1 seconds debounce time

interface SettingsResponse<T> {
  version: number;
  settings: T;
}

async function makeApiRequest<T>(endpoint: string, method: 'GET' | 'POST' | 'PUT' = 'GET', body?: any): Promise<Result<T, ApiError>> {
  try {
    const { firebaseAuth } = await import('lib/firebase/firebase.client');
    const authToken = await firebaseAuth.currentUser?.getIdToken(true);

    if (!authToken) {
      return Err({
        type: ErrorType.UNKNOWN_ERROR,
        message: 'No user token available'
      });
    }

    const url = new URL(`${baseUrl}/settings${endpoint}`);

    const response = await fetch(url.toString(), {
      method,
      headers: {
        Authorization: `Bearer ${authToken}`,
        'X-Speechify-Client': 'WebApp',
        'X-Speechify-Client-Version': getStringEnvWithDefault('version', '0.0.0'),
        'Content-Type': 'application/json'
      },
      body: body ? JSON.stringify(body) : undefined
    });

    // 304 - content not modified
    if (response.status === 304) {
      return Err({
        type: ErrorType.NOT_MODIFIED,
        message: 'Passed version is up to date',
        statusCode: 304
      });
    }

    // 409 - version mismatch
    if (response.status === 409) {
      return Err({
        type: ErrorType.VERSION_MISMATCH,
        message: 'Version mismatch detected',
        statusCode: 409
      });
    }

    // 400 - invalid data or no changes
    if (response.status === 400) {
      const errorData = await response.json();

      if (errorData.message === 'No changes to settings') {
        return Err({
          type: ErrorType.NOT_MODIFIED,
          message: 'No changes to settings',
          statusCode: 400
        });
      }

      return Err({
        type: ErrorType.INVALID_REQUEST,
        message: errorData.message || 'Invalid values passed or no changes detected',
        statusCode: 400
      });
    }

    // success
    if (response.ok) {
      const data = await response.json();
      return Ok(data as T);
    }

    // unknown
    return Err({
      type: ErrorType.UNKNOWN_ERROR,
      message: `Request failed with status ${response.status}`,
      statusCode: response.status
    });
  } catch (error) {
    // network errors
    return Err({
      type: ErrorType.NETWORK_ERROR,
      message: error instanceof Error ? error.message : 'Network request failed'
    });
  }
}

function mapSettings(data: Record<string, any>, direction: 'toShared' | 'fromShared' = 'toShared'): Record<string, any> {
  const userSettingsMap: Record<string, string> = {
    [UserSettingsEnum.AUTO_HIDE_INTERFACE]: SharedUserSettingsEnum.AUTO_HIDE_PLAYER,
    [UserSettingsEnum.AUTO_SCROLL]: SharedUserSettingsEnum.AUTO_SCROLL,
    [UserSettingsEnum.AUTO_SPEED_UP]: SharedUserSettingsEnum.AUTO_SPEED_UP,
    [UserSettingsEnum.CLICK_TO_LISTEN]: SharedUserSettingsEnum.CLICK_TO_LISTEN,
    [UserSettingsEnum.CURRENT_VOICE]: SharedUserSettingsEnum.CURRENT_VOICE,
    [UserSettingsEnum.PLAYBACK_SPEED]: SharedUserSettingsEnum.PLAYBACK_SPEED,
    [UserSettingsEnum.RECENT_VOICES]: SharedUserSettingsEnum.RECENT_VOICES,
    [UserSettingsEnum.WEB_CURRENT_VOICE]: SharedUserSettingsEnum.WEB_CURRENT_VOICE,

    [UserSettingsEnum.SKIP_BRACES]: SharedUserSettingsEnum.AUTO_SKIP_BRACES,
    [UserSettingsEnum.SKIP_BRACKETS]: SharedUserSettingsEnum.AUTO_SKIP_BRACKETS,
    [UserSettingsEnum.SKIP_CITATIONS]: SharedUserSettingsEnum.AUTO_SKIP_CITATIONS,
    [UserSettingsEnum.SKIP_ENHANCED]: SharedUserSettingsEnum.AUTO_SKIP_ENHANCED,
    [UserSettingsEnum.SKIP_FOOTERS]: SharedUserSettingsEnum.AUTO_SKIP_FOOTERS,
    [UserSettingsEnum.SKIP_FOOTNOTES]: SharedUserSettingsEnum.AUTO_SKIP_FOOTNOTES,
    [UserSettingsEnum.SKIP_HEADERS]: SharedUserSettingsEnum.AUTO_SKIP_HEADERS,
    [UserSettingsEnum.SKIP_PARENTHESES]: SharedUserSettingsEnum.AUTO_SKIP_PARENTHESES,
    [UserSettingsEnum.SKIP_URLS]: SharedUserSettingsEnum.AUTO_SKIP_URLS,
    [UserSettingsEnum.SKIP_FORMULAS]: SharedUserSettingsEnum.AUTO_SKIP_FORMULAS,
    [UserSettingsEnum.SKIP_TABLES]: SharedUserSettingsEnum.AUTO_SKIP_TABLES,

    [SharedUserSettingsEnum.LIBRARY_IMPORT_SUGGEST_FILES]: SharedUserSettingsEnum.LIBRARY_IMPORT_SUGGEST_FILES,
    [SharedUserSettingsEnum.FAVORITE_VOICES]: SharedUserSettingsEnum.FAVORITE_VOICES,

    [SharedUserSettingsEnum.DAILY_GOAL_TARGET_MINUTES]: SharedUserSettingsEnum.DAILY_GOAL_TARGET_MINUTES
  };

  const mappedData: Record<string, any> = {};

  const sourceMap =
    direction === 'toShared'
      ? userSettingsMap
      : Object.entries(userSettingsMap).reduce(
          (acc, [key, value]) => {
            acc[value] = key;
            return acc;
          },
          {} as Record<string, string>
        );

  Object.entries(data).forEach(([key, value]) => {
    if (sourceMap[key]) {
      const targetKey = sourceMap[key];

      if (direction === 'toShared') {
        if (key === UserSettingsEnum.CURRENT_VOICE) {
          mappedData[targetKey] = value?.id || value;
        } else if (key === UserSettingsEnum.RECENT_VOICES) {
          mappedData[targetKey] = value?.map((voice: any) => voice?.id || voice).filter((voice: any) => voice !== undefined && voice !== null);
        } else {
          mappedData[targetKey] = value;
        }
      }

      if (direction === 'fromShared') {
        mappedData[targetKey] = value;
      }
    }
  });

  return mappedData;
}

async function fetchUserSettings(): Promise<Result<void, ApiError>> {
  const store = useSettingsStore.getState();
  const context = SettingsContext.WEB_APP;
  const version = store.userSettings.version;

  const endpoint = `/user/latest?context=${context}&version=${version}`;
  const result = await makeApiRequest<SettingsResponse<any>>(endpoint);

  if (isErr(result)) {
    // if not modified, we're still in sync
    if (result.error.type === ErrorType.NOT_MODIFIED) {
      return Ok(undefined);
    }

    if (result.error.type === ErrorType.NETWORK_ERROR && result.error.message === 'Network request failed') {
      return Ok(undefined);
    }

    // Track error
    await trackSyncError(result.error);
    return Err(result.error);
  }

  useSettingsStore.setState({
    userSettings: {
      version: result.value.version,
      settings: result.value.settings
    },
    syncStatus: {
      ...store.syncStatus,
      lastFetchTimestamp: Date.now()
    },
    isLoading: false
  });

  return Ok(undefined);
}

async function updateUserSettings(settings: Record<string, any>): Promise<Result<void, ApiError>> {
  const store = useSettingsStore.getState();
  const context = SettingsContext.WEB_APP;
  const version = store.userSettings.version;

  const mappedSettings = mapSettings(settings, 'toShared');

  const body = {
    context,
    version,
    settings: mappedSettings
  };

  const result = await makeApiRequest<SettingsResponse<any>>('/user/update', 'POST', body);

  if (isErr(result)) {
    if (result.error.type === ErrorType.VERSION_MISMATCH) {
      // handle version mismatch by fetching latest and then retrying
      const fetchResult = await fetchUserSettings();

      if (isErr(fetchResult)) {
        return fetchResult;
      }

      // retry update with fresh version
      return updateUserSettings(settings);
    } else if (result.error.type === ErrorType.NOT_MODIFIED) {
      return Ok(undefined);
    }

    // track error
    await trackSyncError(result.error);
    return Err(result.error);
  }

  // update store
  useSettingsStore.setState({
    userSettings: {
      version: result.value.version,
      settings: result.value.settings
    },
    syncStatus: {
      ...store.syncStatus,
      lastFetchTimestamp: Date.now()
    }
  });

  return Ok(undefined);
}

let pendingSyncTimer: ReturnType<typeof setTimeout> | null = null;

export function queueSettingsChange(settings: Record<string, any>): void {
  const store = useSettingsStore.getState();

  // apply optimistic update to the store local state
  useSettingsStore.setState(state => ({
    userSettings: {
      ...state.userSettings,
      settings: {
        ...state.userSettings.settings,
        ...settings
      }
    }
  }));

  const pendingChanges = [...store.syncStatus.pendingChanges];

  pendingChanges.push({
    data: settings,
    timestamp: Date.now()
  });

  useSettingsStore.setState({
    syncStatus: {
      ...store.syncStatus,
      pendingChanges
    }
  });

  if (pendingSyncTimer) {
    clearTimeout(pendingSyncTimer);
  }

  // batch changes together for the next SYNC_DEBOUNCE_TIME
  pendingSyncTimer = setTimeout(() => {
    syncPendingChanges().catch(error => {
      console.error('Failed to sync settings changes', error);
    });

    pendingSyncTimer = null;
  }, SYNC_DEBOUNCE_TIME);
}

async function syncPendingChanges(): Promise<Result<void, ApiError>> {
  const store = useSettingsStore.getState();
  const { pendingChanges } = store.syncStatus;

  if (pendingChanges.length === 0) {
    return Ok(undefined);
  }

  const mergedChanges = pendingChanges.reduce(
    (merged, change) => ({
      ...merged,
      ...change.data
    }),
    {}
  );

  const result = await updateUserSettings(mergedChanges);

  if (isErr(result)) {
    return Err(result.error);
  }

  useSettingsStore.setState({
    syncStatus: {
      ...store.syncStatus,
      pendingChanges: []
    }
  });

  return Ok(undefined);
}

async function syncSettings(forceRefresh = false): Promise<Result<void, ApiError>> {
  const store = useSettingsStore.getState();
  const { lastFetchTimestamp } = store.syncStatus;

  if (!forceRefresh && lastFetchTimestamp + SYNC_CACHE_TTL > Date.now()) {
    // cache is fresh, just sync pending changes
    if (isDev) console.info('Settings sync cache is fresh, just sync pending changes');

    return syncPendingChanges();
  }

  // fetch latest settings
  const fetchResult = await fetchUserSettings();

  if (isErr(fetchResult)) {
    return fetchResult;
  }

  // sync any pending changes
  return syncPendingChanges();
}

async function trackSyncError(error: ApiError): Promise<void> {
  const store = useSettingsStore.getState();
  const { syncErrors } = store.syncStatus;

  useSettingsStore.setState({
    syncStatus: {
      ...store.syncStatus,
      syncErrors: {
        count: syncErrors.count + 1,
        lastError: error.message,
        lastErrorTime: Date.now()
      }
    }
  });

  logError(new Error(`Settings sync error: ${JSON.stringify(error)}`), undefined, {
    context: {
      errorMessage: error.message,
      syncErrorCount: String(syncErrors.count + 1)
    }
  });
}

export function resetSyncErrors(): void {
  const store = useSettingsStore.getState();

  if (store.syncStatus.syncErrors.count > 0) {
    useSettingsStore.setState({
      syncStatus: {
        ...store.syncStatus,
        syncErrors: {
          count: 0,
          lastError: '',
          lastErrorTime: 0
        }
      }
    });
  }
}

export function initSettingsSync() {
  const syncState = {
    isRunning: false,
    lastSuccess: 0,
    failureCount: 0,
    timeoutId: null as NodeJS.Timeout | null
  };

  const attemptInitialSync = async (retryCount = 0, forceRefresh?: boolean) => {
    if (syncState.isRunning) return;
    syncState.isRunning = true;

    // evaluate force refresh only on the first attempt
    let shouldForceRefresh = forceRefresh;

    if (retryCount === 0 && forceRefresh === undefined) {
      const isItemPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/item/');
      shouldForceRefresh = isItemPage;
    }

    const result = await syncSettings(shouldForceRefresh);

    if (isErr(result)) {
      syncState.failureCount++;
      const backoff = Math.min(30000, 1000 * Math.pow(2, retryCount)); // max 30s backoff
      console.error(`Settings sync failed, retrying in ${backoff}ms`, result.error);
      setTimeout(() => attemptInitialSync(retryCount + 1, shouldForceRefresh), backoff);
    } else {
      syncState.lastSuccess = Date.now();
      syncState.failureCount = 0;
    }

    syncState.isRunning = false;
  };

  const scheduledSync = async (forceRefresh = false) => {
    if (syncState.isRunning) return;
    syncState.isRunning = true;

    const result = await syncSettings(forceRefresh);

    if (isErr(result)) {
      syncState.failureCount++;
      logError(new Error(`Scheduled sync failed: ${result.error.message}`));
    } else {
      syncState.lastSuccess = Date.now();
      syncState.failureCount = 0;
    }

    syncState.isRunning = false;

    // schedule next sync incase page remains visible
    if (document.visibilityState === 'visible') {
      syncState.timeoutId = setTimeout(scheduledSync, SYNC_CACHE_TTL);
    }
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      scheduledSync(true); // force refresh on visibility
    } else if (syncState.timeoutId) {
      clearTimeout(syncState.timeoutId);
      syncState.timeoutId = null;
    }
  };

  attemptInitialSync();

  document.addEventListener('visibilitychange', handleVisibilityChange);

  handleVisibilityChange();

  // cleanup
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);

    if (syncState.timeoutId) {
      clearTimeout(syncState.timeoutId);
      syncState.timeoutId = null;
    }
  };
}
